import React, { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import moment from "moment";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
import { defaultThemeColors, readThemeColors } from "../../utils/waChartTheme";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend
);

const VooruitgangChart = ({
  selectedMetric,
  setSelectedMetric,
  metricData,
  averageScore,
}) => {
  const [themeColors, setThemeColors] = useState(defaultThemeColors);

  useEffect(() => {
    const sync = () => setThemeColors(readThemeColors());
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "style"],
    });
    window.addEventListener("storage", sync);
    return () => {
      observer.disconnect();
      window.removeEventListener("storage", sync);
    };
  }, []);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      color: themeColors.tick,
      scales: {
        x: {
          ticks: { color: themeColors.tick, maxRotation: 45, minRotation: 0 },
          grid: { color: themeColors.grid },
          border: { color: themeColors.grid },
        },
        y: {
          ticks: { color: themeColors.tick },
          grid: { color: themeColors.grid },
          border: { color: themeColors.grid },
        },
      },
      plugins: {
        legend: {
          labels: {
            color: themeColors.legend,
            usePointStyle: true,
            pointStyle: "rectRounded",
          },
        },
        tooltip: {
          callbacks: {
            title: function (tooltipItems) {
              return `${tooltipItems[0].label}`;
            },
            label: function (tooltipItem) {
              const index = tooltipItem?.dataIndex;
              const rawData = metricData[selectedMetric]?.[index];
              return `Score: ${tooltipItem?.raw} ${
                rawData?.uitleg ? " | Meer uitleg : " + rawData?.uitleg : ""
              }`;
            },
            footer: function (tooltipItems) {
              return `Extra Info: ${tooltipItems.length} points`;
            },
          },
          enabled: false,
          external: function (context) {
            let tooltipEl = document.getElementById("chartjs-tooltip-vooruitgang-client");
            if (!tooltipEl) {
              tooltipEl = document.createElement("div");
              tooltipEl.id = "chartjs-tooltip-vooruitgang-client";
              tooltipEl.style.position = "absolute";
              tooltipEl.style.padding = "0px";
              tooltipEl.style.pointerEvents = "none";
              tooltipEl.style.transition = "all 0.2s ease";
              tooltipEl.style.zIndex = "50";
              document.body.appendChild(tooltipEl);
            }

            const tooltipModel = context?.tooltip;
            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = "0";
              return;
            }

            if (tooltipModel?.body) {
              const index = tooltipModel?.dataPoints[0]?.dataIndex;
              const rawData = metricData[selectedMetric]?.[index];

              let innerHtml = `
            <div class="text-xs"><strong style="color:${themeColors.legend}">Datum:</strong> <span style="color:${themeColors.legend}">${tooltipModel?.title[0]}</span></div>
            <div class="text-xs flex items-center gap-x-1" style="margin-top:4px">
              <div style="width:10px;height:10px;border-radius:3px;background:${themeColors.line}"></div>
              <strong style="color:${themeColors.legend}">Score:</strong> <span style="color:${themeColors.legend}">${tooltipModel?.dataPoints[0].raw}</span>
            </div>`;

              if (rawData?.uitleg) {
                innerHtml += `
            <div class="text-xs mt-2" style="color:${themeColors.tick}"><strong style="color:${themeColors.legend}">Meer info:</strong></div>
            <div class="text-xs" style="color:${themeColors.legend}">${rawData?.uitleg}</div>`;
              }

              tooltipEl.innerHTML = sanitizeHtml(
                `<div class="max-w-56 rounded-lg border p-3 shadow-lg" style="background:${themeColors.tooltipBg};border-color:${themeColors.tooltipBorder}">` +
                  innerHtml +
                  "</div>"
              );
            }

            const canvas = context.chart.canvas;
            const position = canvas.getBoundingClientRect();
            tooltipEl.style.opacity = "1";
            tooltipEl.style.left =
              position.left + window.pageXOffset + tooltipModel.caretX - 90 + "px";
            tooltipEl.style.top =
              position.top + window.pageYOffset + tooltipModel.caretY + "px";
          },
        },
      },
    }),
    [metricData, selectedMetric, themeColors]
  );

  const lineData = useMemo(
    () => ({
      labels: metricData[selectedMetric]?.map(
        (item) => `Dag ${moment(item.date).format("L")}`
      ),
      datasets: [
        {
          label: `${selectedMetric} Niveau`,
          data: metricData[selectedMetric]?.map((item) => item.score),
          borderColor: themeColors.line,
          backgroundColor: themeColors.fill,
          fill: true,
          tension: 0.35,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: themeColors.line,
          pointBorderColor: themeColors.tooltipBg,
          borderWidth: 2,
        },
      ],
    }),
    [metricData, selectedMetric, themeColors]
  );

  const score = averageScore?.[selectedMetric];
  const scorePositive = Number(score) > 5;

  return (
    <div className="mb-6 overflow-hidden rounded-2xl border border-wa-border bg-wa-panel shadow-[0_20px_60px_-36px_rgba(0,0,0,0.7)]">
      <div className="border-b border-wa-border bg-gradient-to-br from-wa-panel via-wa-panel2/80 to-wa-panel px-4 py-4 sm:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-wa-text">Vooruitgang</h2>
            <p className="mt-0.5 text-xs text-wa-muted">Evolutie per metriek</p>
          </div>
          <div className="flex flex-wrap items-center gap-3 sm:justify-end">
            <select
              id="time-filter"
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="min-w-[12rem] rounded-lg border border-wa-border bg-wa-panel2 px-3 py-2 text-sm font-medium text-wa-text shadow-sm outline-none transition-colors focus:border-wa-accent/50 focus:ring-2 focus:ring-wa-accent/25"
            >
              <option value="slaap">Slaapkwaliteit</option>
              <option value="energie">Energieniveau</option>
              <option value="stress">Stressniveau</option>
              <option value="cravings">Cravings</option>
            </select>
            <div className="flex items-center gap-2 rounded-full border border-wa-border bg-wa-panel2/80 px-3 py-1.5">
              <span className="text-xs font-medium text-wa-muted">Gemiddelde score</span>
              <span
                className={`inline-flex min-w-[2.25rem] items-center justify-center rounded-full px-2.5 py-0.5 text-sm font-semibold ${
                  scorePositive
                    ? "bg-wa-accent/20 text-wa-accent"
                    : "bg-red-900/50 text-red-100"
                }`}
              >
                {score}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="min-h-[280px] p-4 sm:h-96 sm:min-h-[320px]">
        <div className="h-full min-h-[240px]">
          <Line data={lineData} options={chartOptions} />
        </div>
      </div>
    </div>
  );
};

export default VooruitgangChart;
